import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {GammaAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/gamma-adjustment-option';
import {TintAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/tint-adjustment-option';
import {MultiplyAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/multiply-adjustment-option';
import {DefaultPosterEditorItemAdjustmentOptions} from '@Panels/poster-editor-item-adjustments-panel/components/default-adjustment-options';
import {PosterEditorItemAdjustmentOptions} from '@Panels/poster-editor-item-adjustments-panel/components/default-adjustment-options/default-adjustment-options.types';
import {BrightnessAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/brightness-adjustment-option';
import {SaturationAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/saturation-adjustment-option';
import {VibranceAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/vibrance-adjustment-option';
import {ContrastAdjusmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/contrast-adjusment-option';
import {TintColorPanel} from '@Panels/poster-editor-item-adjustments-panel/components/tint-color-panel';
import {MultiplyColorPanel} from '@Panels/poster-editor-item-adjustments-panel/components/multiply-color-panel';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenu} from '@Components/contextual-menu';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorItemAdjustmentsPanelProps {
  panelId: string;
}

enum AdjustmentPanels {
  DEFAULT = 'DEFAULT',
  MULTIPLY_COLOR_PICKER = 'MULTIPLY_COLOR_PICKER',
  TINT_COLOR_PICKER = 'COLOR_PICKER',
}

export default function PosterEditorItemAdjustmentsPanel({...props}: PosterEditorItemAdjustmentsPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [selectedSubPanel, setSelectedSubPanel] = useState(PosterEditorItemAdjustmentOptions.NONE);
  const [panelToShow, setPanelToShow] = useState(AdjustmentPanels.DEFAULT);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  useEffect((): void => {
    updateOptionsPanelHeight(props.panelId, '144px');
  }, []);

  const getHeaderTitle = (): string => {
    switch (selectedSubPanel) {
      case PosterEditorItemAdjustmentOptions.BRIGHTNESS:
        return window.i18next.t('pmwjs_brightness');
      case PosterEditorItemAdjustmentOptions.GAMMA:
        return window.i18next.t('pmwjs_gamma');
      case PosterEditorItemAdjustmentOptions.MULTIPLY:
        return window.i18next.t('pmwjs_multiply');
      case PosterEditorItemAdjustmentOptions.TINT:
        return window.i18next.t('pmwjs_tint');
      case PosterEditorItemAdjustmentOptions.VIBRANCE:
        return window.i18next.t('pmwjs_vibrance');
      case PosterEditorItemAdjustmentOptions.SATURATION:
        return window.i18next.t('pmwjs_saturation');
      case PosterEditorItemAdjustmentOptions.CONTRAST:
        return window.i18next.t('pmwjs_contrast');
      case PosterEditorItemAdjustmentOptions.NONE:
      default:
        return window.i18next.t('pmwjs_adjustments');
    }
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader showBackButton={selectedSubPanel !== PosterEditorItemAdjustmentOptions.NONE} onBackButton={onBackButton} onClose={onClose} title={getHeaderTitle()} />;
  };

  const onBackButton = (): void => {
    setSelectedSubPanel(PosterEditorItemAdjustmentOptions.NONE);
  };

  const onClickHandler = (clickedId: PosterEditorItemAdjustmentOptions): void => {
    setSelectedSubPanel(clickedId);
  };

  const getDefaultContent = (): ReactElement => {
    return <DefaultPosterEditorItemAdjustmentOptions panelId={props.panelId} onClick={onClickHandler} />;
  };

  const getBrightnessOption = (): ReactElement | null => {
    return <BrightnessAdjustmentOption panelId={props.panelId} />;
  };

  const getSaturationOption = (): ReactElement | null => {
    return <SaturationAdjustmentOption panelId={props.panelId} />;
  };

  const getVibranceOption = (): ReactElement | null => {
    return <VibranceAdjustmentOption panelId={props.panelId} />;
  };

  const getContrastOption = (): ReactElement | null => {
    return <ContrastAdjusmentOption panelId={props.panelId} />;
  };

  const getGammaOptions = (): ReactElement | null => {
    return <GammaAdjustmentOption panelId={props.panelId} />;
  };

  const getTintOption = (): ReactElement | null => {
    return (
      <TintAdjustmentOption
        onBack={(): void => {
          setSelectedSubPanel(PosterEditorItemAdjustmentOptions.NONE);
        }}
        onClickColorFill={(): void => {
          setPanelToShow(AdjustmentPanels.TINT_COLOR_PICKER);
        }}
        panelId={props.panelId}
      />
    );
  };

  const getMultiplyOption = (): ReactElement | null => {
    return (
      <MultiplyAdjustmentOption
        onClickColorFill={(): void => {
          setPanelToShow(AdjustmentPanels.MULTIPLY_COLOR_PICKER);
        }}
        panelId={props.panelId}
      />
    );
  };

  const getContentFromState = (): ReactElement | null => {
    switch (selectedSubPanel) {
      case PosterEditorItemAdjustmentOptions.BRIGHTNESS:
        return getBrightnessOption();
      case PosterEditorItemAdjustmentOptions.SATURATION:
        return getSaturationOption();
      case PosterEditorItemAdjustmentOptions.VIBRANCE:
        return getVibranceOption();
      case PosterEditorItemAdjustmentOptions.CONTRAST:
        return getContrastOption();
      case PosterEditorItemAdjustmentOptions.TINT:
        return getTintOption();
      case PosterEditorItemAdjustmentOptions.MULTIPLY:
        return getMultiplyOption();
      case PosterEditorItemAdjustmentOptions.GAMMA:
        return getGammaOptions();
      case PosterEditorItemAdjustmentOptions.NONE:
      default:
        return getDefaultContent();
    }
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu
        id="adjustments-mobile-options"
        hasMobileMorphingProperties
        includeLeftRightPadding={false}
        contentClassName={`spacing-p-b-0 ${
          selectedSubPanel === PosterEditorItemAdjustmentOptions.NONE ? '' : 'spacing-p-l-4 spacing-p-r-4 spacing-p-b-4'
        } spacing-p-t-1 spacing-m-0`}
      >
        {getContentFromState()}
      </ContextualMenu>
    );
  };

  const getTintColorPicker = (): ReactElement | null => {
    return (
      <TintColorPanel
        panelId={props.panelId}
        onBack={(): void => {
          setPanelToShow(AdjustmentPanels.DEFAULT);
        }}
        onClose={onClose}
      />
    );
  };

  const getMultiplyColorPicker = (): ReactElement | null => {
    return (
      <MultiplyColorPanel
        panelId={props.panelId}
        onBack={(): void => {
          setPanelToShow(AdjustmentPanels.DEFAULT);
        }}
        onClose={onClose}
      />
    );
  };

  const getDefaultPanel = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  const getPanels = (): ReactElement | null => {
    switch (panelToShow) {
      case AdjustmentPanels.DEFAULT:
        return getDefaultPanel();
      case AdjustmentPanels.TINT_COLOR_PICKER:
        return getTintColorPicker();
      default:
        return getMultiplyColorPicker();
    }
  };

  return getPanels();
}
